import type { ProductSize } from '../types/order';

/**
 * Τιμολόγηση καταλόγου στο ταμείο — ΔΙΔΥΜΟ του backend/utils/catalogPrice.js.
 *
 * Φ5 της κατάργησης της τιμής προϊόντος: «η τιμή ζει στο μέγεθος» (docs/base_price_phase5_plan.md
 * §3). Μετά το κλείδωμα της Φ4 κανένα προϊόν μη-τέλος δεν έχει τιμή σε επίπεδο προϊόντος, άρα
 * εδώ ΔΕΝ διαβάζεται καμία στήλη τιμής προϊόντος· τα τέλη παίρνουν το ποσό τους μόνο από το
 * state_fees (απόφαση 1_fees_source), μέσω του fee_unit_gross που στέλνει ο server.
 *
 * Οι κοινοί ορισμοί αλλάζουν ΜΟΝΟ μαζί στα δύο αρχεία. Φρουρός της ισοδυναμίας είναι το κοινό
 * fixture backend/tests/fixtures/pricing_cases.json (pricingCases.test.ts εδώ, catalogPrice.test.js
 * εκεί): αν ο server κι εδώ διάλεγαν άλλο προεπιλεγμένο μέγεθος ή άλλο ποσό τέλους, ο ταμίας θα
 * έβλεπε μία τιμή και ο server θα έκοβε την παραγγελία (ή θα χρέωνε άλλη).
 */

type Channel = 'take_away' | 'delivery' | 'dine_in';

/** Ό,τι χρειάζεται από ένα μέγεθος για σειρά/πωλησιμότητα/προεπιλογή. */
type SizeLike = {
  id: number | string;
  is_default?: boolean | null;
  is_available?: boolean | null;
  is_hidden?: boolean | null;
  display_order?: number | null;
};

/** Ό,τι χρειάζεται από ένα μέγεθος για την τιμή του. */
type SizePrice = Pick<ProductSize, 'price_modifier' | 'price_modifier_take_away' | 'price_modifier_delivery' | 'price_modifier_dine_in'>;

/** Ό,τι χρειάζεται από ένα προϊόν για να ξέρουμε αν είναι τέλος και πόσο κοστίζει. */
type FeeLike = {
  id?: number | string;
  fee_category?: number | null;
  fee_unit_gross?: number | string | null;
};

/* ───────────────────────── Μεγέθη ───────────────────────── */

/**
 * Ποια μεγέθη πουλάει το ταμείο (§3.1).
 *
 * Ίδιος κανόνας με το backend (routes/products.js, GET /stores/:id/products με
 * available_only=true και catalogPrice.isSizeSellable). Τα NULL μετρούν ως διαθέσιμο /
 * όχι κρυφό. Ζει εδώ (και όχι στο sellableSizes.ts, που το ξανα-εξάγει) γιατί το
 * sellableSizes εισάγει ήδη από το pricing — αντίστροφα θα γινόταν κυκλικό import.
 *
 * - is_hidden: το κατάστημα έκρυψε το μέγεθος («δεν έχουμε πια XL»)
 * - is_available false: αφαιρέθηκε κεντρικά από τον οργανισμό
 */
export function isSizeSellable(size: { is_available?: boolean | null; is_hidden?: boolean | null }): boolean {
  return size.is_available !== false && size.is_hidden !== true;
}

/**
 * §3.2: display_order ASC NULLS LAST, id ASC — η σειρά του JSON_AGG της λίστας POS.
 * Δεν αλλάζει τον πίνακα εισόδου.
 */
export function sortSizes<T extends SizeLike>(sizes: T[] | null | undefined): T[] {
  return [...(sizes || [])].sort((a, b) => {
    const da = a.display_order == null ? Infinity : Number(a.display_order);
    const db = b.display_order == null ? Infinity : Number(b.display_order);
    if (da !== db) return da - db;
    return Number(a.id) - Number(b.id);
  });
}

/**
 * §3.3: το πρώτο ΠΩΛΗΣΙΜΟ με is_default === true κατά σειρά, αλλιώς το πρώτο πωλήσιμο,
 * αλλιώς null.
 *
 * Το is_default NULL ΔΕΝ είναι default (ίδιο με το find(s => s.is_default)). Στη λίστα POS,
 * που έχει ήδη μόνο πωλήσιμα και είναι ταξινομημένη, δίνει ό,τι το παλιό
 * `sizes.find(is_default) || sizes[0]`· η διαφορά φαίνεται μόνο σε λίστες με κρυφά μεγέθη
 * (delivery/dine-in πριν το withSellableSizes), όπου ένα κρυφό default δεν πρέπει να
 * προτείνεται — το ίδιο διαλέγει και ο server για γραμμή χωρίς μέγεθος (απόφαση 12).
 */
export function defaultSize<T extends SizeLike>(sizes: T[] | null | undefined): T | null {
  const S = sortSizes((sizes || []).filter(isSizeSellable));
  return S.find(s => s.is_default === true) ?? S[0] ?? null;
}

/**
 * Το μέγεθος που τιμολογεί μια γραμμή/πλακίδιο ΧΩΡΙΣ ρητό μέγεθος: το defaultSize, και αν
 * κανένα δεν είναι πωλήσιμο το πρώτο όλων κατά σειρά — ίδιο με το
 * defaultSize(sizes, {includeUnsellable: true}) του server. Αλλιώς ένα προϊόν με όλα τα
 * μεγέθη κρυφά θα έδειχνε/χρέωνε 0 € εδώ ενώ ο server το τιμολογεί κανονικά.
 */
function pricingSize<T extends SizeLike>(sizes: T[] | null | undefined): T | null {
  return defaultSize(sizes) ?? sortSizes(sizes)[0] ?? null;
}

/**
 * Απόλυτη μικτή τιμή ενός μεγέθους στο κανάλι (§3.5): price_modifier_<κανάλι> ?? price_modifier.
 */
export function sizeChannelPrice(
  size: SizePrice,
  channel: Channel = 'take_away'
): number {
  const fallback = Number(size.price_modifier) || 0;
  const override =
    channel === 'delivery' ? size.price_modifier_delivery
    : channel === 'dine_in' ? size.price_modifier_dine_in
    : size.price_modifier_take_away;
  if (override === null || override === undefined || (override as unknown) === '') return fallback;
  const n = Number(override);
  return Number.isFinite(n) ? n : fallback;
}

/* ───────────────────────── Τέλη ───────────────────────── */

/** §3.4: CHECK 179 — products.fee_category ∈ {8, 16}· οτιδήποτε μη NULL είναι τέλος. */
export function isFeeProduct(p: { fee_category?: number | null }): boolean {
  return p.fee_category != null;
}

// Ένα μήνυμα ανά προϊόν: το feeUnitGross καλείται σε κάθε render του πλακιδίου και θα
// πλημμύριζε την κονσόλα.
const warnedMissingFee = new Set<string>();

/**
 * §3.9: μικτό ποσό τέλους ανά τεμάχιο (απόφαση 1_fees_source: ROUND(state_fees × 1,24, 2)).
 *
 * Το ποσό το υπολογίζει ο SERVER και το στέλνει ως fee_unit_gross στη λίστα POS από τη Φ1·
 * το ταμείο ΔΕΝ πολλαπλασιάζει ×1,24, για να μην υπάρχουν δύο στρογγυλοποιήσεις.
 *
 * - μη τέλος → null·
 * - fee_unit_gross θετικός αριθμός → αυτό·
 * - αλλιώς (λείπει η γραμμή state_fees, είναι ανενεργή, ή το πεδίο δεν ήρθε καθόλου) → null με
 *   console.error, ώστε να φαίνεται στη διάγνωση αντί να χρεωθεί σιωπηλά ένα ποσό που δεν
 *   επιβεβαιώνεται. Καμία εφεδρεία σε τιμή του προϊόντος (Φ5): ο καθρέφτης δεν γράφεται πια.
 */
export function feeUnitGross(p: FeeLike): number | null {
  if (!isFeeProduct(p)) return null;
  const n = Number(p.fee_unit_gross);
  // Number(null) = 0 → πέφτει κι αυτό στο null, όπως το '0'· Number(undefined) = NaN.
  if (p.fee_unit_gross !== null && p.fee_unit_gross !== undefined && Number.isFinite(n) && n > 0) return n;
  const key = String(p.id ?? '?');
  if (!warnedMissingFee.has(key)) {
    warnedMissingFee.add(key);
    console.error(`[fee] λείπει ποσό state_fees για το προϊόν ${key} (fee_category ${p.fee_category})`);
  }
  return null;
}

/* ───────────────────────── Προϊόν ───────────────────────── */

type PricedProduct = FeeLike & {
  sizes?: Array<SizeLike & SizePrice> | null;
};

/**
 * Τιμή εμφάνισης (πλακίδιο, γρήγορη προσθήκη, τιμή κιλού): τέλος → feeUnitGross (0 αν λείπει)·
 * αλλιώς η τιμή καναλιού του προεπιλεγμένου μεγέθους. Δίδυμο του displayPriceSql του server.
 * Μη-τέλος χωρίς κανένα μέγεθος (ανωμαλία μετά το κλείδωμα) → 0.
 */
export function productDisplayPrice(
  product: PricedProduct,
  channel: Channel = 'take_away'
): number {
  return lineUnitPrice(product, null, channel);
}

/**
 * Τιμή μονάδας γραμμής (ευρώ) — δίδυμο του catalogPrice.linePrice:
 *  - τέλος → feeUnitGross (το μέγεθος αγνοείται· 0 αν λείπει το ποσό, και τότε οι οθόνες δεν
 *    βάζουν καθόλου γραμμή — απόφαση 33)·
 *  - αλλιώς → τιμή καναλιού του μεγέθους της γραμμής· χωρίς μέγεθος το προεπιλεγμένο (απόφαση 12:
 *    όπως τιμολογεί ο server, όχι 0 €)·
 *  - μη-τέλος χωρίς ΚΑΝΕΝΑ μέγεθος → 0. Ο server δίνει null και κατώτατο 0 με [OPS-ALERT]
 *    NO_SIZE_PRODUCT· εδώ 0, ώστε η οθόνη να συμφωνεί με το κατώτατο.
 */
export function lineUnitPrice(
  product: PricedProduct,
  size: SizePrice | null | undefined,
  channel: Channel = 'take_away'
): number {
  if (isFeeProduct(product)) return feeUnitGross(product) ?? 0;
  const s = size ?? pricingSize(product.sizes);
  return s ? sizeChannelPrice(s, channel) : 0;
}

/**
 * §3.7: σύνολο γραμμής ζυγιζόμενου (ευρώ) με ΜΙΑ στρογγυλοποίηση — δίδυμο του
 * catalogPrice.weightedLineCents: Math.round(λεπτά μονάδας × βάρος). Ίδια στρογγυλοποίηση με το
 * κατώτατο του server, αλλιώς ο ταμίας χωρίς δικαίωμα έκπτωσης θα έτρωγε 403 για ένα λεπτό
 * διαφορά. Μη αριθμητικό βάρος → NaN (το backend δίνει null· ο καλών δεν πρέπει να το στείλει).
 */
export function weightedLineTotal(unitPrice: number, weight: number | string): number {
  return Math.round(Math.round(unitPrice * 100) * Number(weight)) / 100;
}
